/**
 * Animation clip-name contract — PURE DATA (no Babylon import, so the server
 * can depend on @fisura/shared). The client resolves real AnimationGroups
 * against these ordered substring patterns, with graceful fallback so a model
 * with only idle/walk/run still works.
 */
export type ClipRole =
  | 'idle'
  | 'walk_f'
  | 'walk_b'
  | 'walk_l'
  | 'walk_r'
  | 'run'
  | 'crouch_idle'
  | 'crouch_walk'
  | 'jump'
  | 'fall'
  | 'land';

/**
 * For each role, an ordered list of lowercase substrings to match against
 * `AnimationGroup.name`, then a fallback role to reuse if none matched. The
 * client tries patterns in order, then follows `fallback` chains.
 */
export const CLIP_PATTERNS: Record<ClipRole, { match: string[]; fallback?: ClipRole }> = {
  idle: { match: ['idle', 'breathing'] },
  run: { match: ['run', 'sprint', 'jog'], fallback: 'walk_f' },
  walk_f: { match: ['walk_fwd', 'walkforward', 'walk_forward', 'walk_f', 'walking', 'walk'], fallback: 'idle' },
  walk_b: { match: ['walk_bwd', 'walkback', 'walk_back', 'walk_b'], fallback: 'walk_f' },
  walk_l: { match: ['strafe_l', 'walk_left', 'left_strafe', 'walkleft'], fallback: 'walk_f' },
  walk_r: { match: ['strafe_r', 'walk_right', 'right_strafe', 'walkright'], fallback: 'walk_f' },
  crouch_idle: { match: ['crouch_idle', 'crouchidle', 'crouch_pose', 'crouching'], fallback: 'idle' },
  crouch_walk: { match: ['crouch_walk', 'crouchwalk', 'crouch_move', 'sneak'], fallback: 'crouch_idle' },
  jump: { match: ['jump_up', 'jumpup', 'jump_start', 'jump'], fallback: 'idle' },
  fall: { match: ['fall', 'falling', 'air', 'in_air'], fallback: 'jump' },
  land: { match: ['land', 'landing', 'jump_land'], fallback: 'idle' },
};

/** Per-asset rig configuration the loader needs (chosen by model file name). */
export interface AssetConfig {
  /** Added to facing yaw so the model's forward matches +Z when yaw=0. */
  yawOffset: number;
  /** Candidate spine/chest bone names for the aim-pitch / torso-twist override. */
  spineBoneNames: string[];
}

export const DEFAULT_ASSET_CONFIG: AssetConfig = {
  yawOffset: 0,
  spineBoneNames: ['spine2', 'spine1', 'spine_02', 'chest', 'upperchest', 'spine', 'mixamorig:spine1'],
};

/** Per-file overrides (keyed by glb filename). */
export const ASSET_CONFIGS: Record<string, AssetConfig> = {
  // Empirically the Soldier's forward needs +90° so its front aligns with the
  // aim yaw (faces forward when the player faces forward).
  'soldier.glb': {
    yawOffset: Math.PI,
    spineBoneNames: ['mixamorig:Spine1', 'mixamorig:Spine', 'Spine', 'chest'],
  },
};
